import { Box, Divider, Typography } from '@mui/material';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';

import type { AnalyticalVariableListItem } from '@verantos-dev/feature-analytical-variables';

import { AvLibraryListCard, AvLibraryListSkeleton } from './AvLibraryListCard';
import type { AvIneligibilityReason } from '../../utils/avLibraryEligibility';

const MSG_ERROR = 'projects.analyticalVariables.library.loadingError';
const MSG_NO_RESULTS = 'projects.analyticalVariables.library.noResults';
const MSG_EMPTY = 'projects.analyticalVariables.library.addFromLibrary.emptyLibrary';

interface AvLibraryListProps {
  isLoading: boolean;
  isError: boolean;
  searchQuery: string;
  items: AnalyticalVariableListItem[];
  multiple: boolean;
  onSelect: (avId: string) => void;
  isSelected: (avId: string) => boolean;
  inCohortAvIds?: Set<string>;
  nameConflictAvIds?: Set<string>;
  disabledReasons?: Map<string, AvIneligibilityReason>;
}

export const AvLibraryList = ({
  isLoading,
  isError,
  searchQuery,
  items,
  multiple,
  onSelect,
  isSelected,
  inCohortAvIds,
  nameConflictAvIds,
  disabledReasons,
}: AvLibraryListProps) => {
  const { formatMessage } = useIntl();

  if (isLoading) return <AvLibraryListSkeleton />;

  if (isError) {
    return (
      <Typography color="error" variant="body2" sx={{ py: 2 }}>
        {formatMessage({ id: MSG_ERROR })}
      </Typography>
    );
  }

  if (items.length === 0) {
    const msgId = searchQuery.trim() ? MSG_NO_RESULTS : MSG_EMPTY;
    return (
      <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
        {formatMessage({ id: msgId })}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, index) => (
        <Fragment key={item.analyticalVariableId}>
          {index > 0 && <Divider />}
          <AvLibraryListCard
            item={item}
            isSelected={isSelected(item.analyticalVariableId)}
            isInCohort={inCohortAvIds?.has(item.analyticalVariableId)}
            isNameConflict={nameConflictAvIds?.has(item.analyticalVariableId)}
            disabledReason={disabledReasons?.get(item.analyticalVariableId)}
            multiple={multiple}
            onSelect={onSelect}
          />
        </Fragment>
      ))}
    </Box>
  );
};
